export enum UserNotificationType {
	SLACK = "SLACK",
	EMAIL = "EMAIL",
	APP = "APP",
}

export enum UserNotificationContentType {
	NO_USER_FOUND = "NO_USER_FOUND",
	NOT_ENOUGH_CREDITS = "NOT_ENOUGH_CREDITS",
	LIST_PROCESSING_DONE = "LIST_PROCESSING_DONE",
	LIST_DIGEST = "LIST_DIGEST",
	LIST_PROCESSING_ERROR = "LIST_PROCESSING_ERROR",
	DEMO_REMINDER = "DEMO_REMINDER",
	SECOND_DEMO_REMINDER = "SECOND_DEMO_REMINDER",
	LIST_ADDED_TO_CUSTOM_EVENT = "LIST_ADDED_TO_CUSTOM_EVENT",
	LOGGED_OUT_FROM_LINKEDIN = "LOGGED_OUT_FROM_LINKEDIN",
	PAYMENT_FAILED = "PAYMENT_FAILED",
	EMAIL_REPLIED = "EMAIL_REPLIED",
	LINKEDIN_MESSAGE_REPLIED = "LINKEDIN_MESSAGE_REPLIED",
	LINKEDIN_INVITE_ACCEPTED = "LINKEDIN_INVITE_ACCEPTED",
	EMAIL_OPENED = "EMAIL_OPENED",
	EMAIL_LINK_CLICKED = "EMAIL_LINK_CLICKED",
	CONNECTION_SYNC_DONE = "CONNECTION_SYNC_DONE",
	CALENDAR_INVITE_RECEIVED = "CALENDAR_INVITE_RECEIVED",
	TICKET_STAFF_REPLY = "TICKET_STAFF_REPLY",
	TICKET_STATUS_CHANGED = "TICKET_STATUS_CHANGED",
	TICKET_ASSIGNED = "TICKET_ASSIGNED",
	TICKET_CUSTOMER_REPLY = "TICKET_CUSTOMER_REPLY",
	// PREC-4288: the balance warnings, members of the CREDITS preference group.
	CREDITS_LOW = "CREDITS_LOW",
	CREDITS_EMPTY = "CREDITS_EMPTY",
	// PREC-4286: a Profile Tracker's monthly job-change check stopped for lack of credits.
	TRACKER_JOB_CHANGE_NO_CREDITS = "TRACKER_JOB_CHANGE_NO_CREDITS",
}

export enum NotificationCategory {
	SYSTEM = "SYSTEM",
	LIST = "LIST",
	ENRICHMENT = "ENRICHMENT",
	SEQUENCE = "SEQUENCE",
	SCORE = "SCORE",
	CAMPAIGN = "CAMPAIGN",
	LINKEDIN = "LINKEDIN",
	PAYMENT = "PAYMENT",
	CUSTOM_EVENT = "CUSTOM_EVENT",
	CONNECTION = "CONNECTION",
}

export enum NotificationPriority {
	LOW = "LOW",
	MEDIUM = "MEDIUM",
	HIGH = "HIGH",
	URGENT = "URGENT",
}

export enum NotificationEntityType {
	COMPANY = "COMPANY",
	LIST = "LIST",
	PROSPECT = "PROSPECT",
	SEQUENCE = "SEQUENCE",
	ENRICHMENT = "ENRICHMENT",
	SCORE = "SCORE",
	CAMPAIGN = "CAMPAIGN",
	CUSTOM_EVENT = "CUSTOM_EVENT",
}

export enum NotificationFrequency {
	INSTANT = "INSTANT",
	HOURLY = "HOURLY",
	DAILY = "DAILY",
	WEEKLY = "WEEKLY",
	OFF = "OFF",
}

export enum NotificationStatus {
	DRAFT = "DRAFT",
	SENT = "SENT",
	ERROR = "ERROR",
}

/** Preference groups — preferences are managed at the group level, not per event type */
export enum NotificationPreferenceGroup {
	LIST_ACTIVITY = "LIST_ACTIVITY",
	LINKEDIN_ISSUES = "LINKEDIN_ISSUES",
	ENGAGEMENT = "ENGAGEMENT",
	SUPPORT_TICKETS = "SUPPORT_TICKETS",
	CREDITS = "CREDITS",
}

/** Display names for event types — used in the preferences page */
export const EVENT_TYPE_DISPLAY_NAMES: Record<UserNotificationContentType, string> = {
	[UserNotificationContentType.LIST_PROCESSING_DONE]: "List Processing Result",
	[UserNotificationContentType.LIST_DIGEST]: "New Leads Digest",
	[UserNotificationContentType.LIST_PROCESSING_ERROR]: "List Processing Result",
	[UserNotificationContentType.NO_USER_FOUND]: "LinkedIn Account Required",
	[UserNotificationContentType.NOT_ENOUGH_CREDITS]: "Insufficient Credits",
	[UserNotificationContentType.DEMO_REMINDER]: "Demo Reminder",
	[UserNotificationContentType.SECOND_DEMO_REMINDER]: "Demo Ending Soon",
	[UserNotificationContentType.LIST_ADDED_TO_CUSTOM_EVENT]: "List Processing Result",
	[UserNotificationContentType.LOGGED_OUT_FROM_LINKEDIN]: "LinkedIn Disconnected",
	[UserNotificationContentType.PAYMENT_FAILED]: "Payment Failed",
	[UserNotificationContentType.EMAIL_REPLIED]: "Email Reply Received",
	[UserNotificationContentType.LINKEDIN_MESSAGE_REPLIED]: "LinkedIn Message Reply",
	[UserNotificationContentType.LINKEDIN_INVITE_ACCEPTED]: "LinkedIn Invite Accepted",
	[UserNotificationContentType.EMAIL_OPENED]: "Email Opened",
	[UserNotificationContentType.EMAIL_LINK_CLICKED]: "Email Link Clicked",
	[UserNotificationContentType.CONNECTION_SYNC_DONE]: "Connection Sync Complete",
	[UserNotificationContentType.CALENDAR_INVITE_RECEIVED]: "Calendar Invite Received",
	[UserNotificationContentType.TICKET_STAFF_REPLY]: "Support replied",
	[UserNotificationContentType.TICKET_STATUS_CHANGED]: "Ticket status changed",
	[UserNotificationContentType.TICKET_ASSIGNED]: "Ticket assigned to you",
	[UserNotificationContentType.TICKET_CUSTOMER_REPLY]: "Customer replied to a ticket",
	[UserNotificationContentType.CREDITS_LOW]: "Credits running low",
	[UserNotificationContentType.CREDITS_EMPTY]: "Out of credits",
	[UserNotificationContentType.TRACKER_JOB_CHANGE_NO_CREDITS]: "Job-change tracking skipped",
};

/** Default category for each event type */
export const EVENT_TYPE_DEFAULT_CATEGORY: Record<
	UserNotificationContentType,
	NotificationCategory
> = {
	[UserNotificationContentType.LIST_PROCESSING_DONE]: NotificationCategory.LIST,
	[UserNotificationContentType.LIST_DIGEST]: NotificationCategory.LIST,
	[UserNotificationContentType.LIST_PROCESSING_ERROR]: NotificationCategory.LIST,
	[UserNotificationContentType.NO_USER_FOUND]: NotificationCategory.LIST,
	[UserNotificationContentType.NOT_ENOUGH_CREDITS]: NotificationCategory.PAYMENT,
	[UserNotificationContentType.DEMO_REMINDER]: NotificationCategory.SYSTEM,
	[UserNotificationContentType.SECOND_DEMO_REMINDER]: NotificationCategory.SYSTEM,
	[UserNotificationContentType.LIST_ADDED_TO_CUSTOM_EVENT]: NotificationCategory.CUSTOM_EVENT,
	[UserNotificationContentType.LOGGED_OUT_FROM_LINKEDIN]: NotificationCategory.LINKEDIN,
	[UserNotificationContentType.PAYMENT_FAILED]: NotificationCategory.PAYMENT,
	[UserNotificationContentType.EMAIL_REPLIED]: NotificationCategory.CAMPAIGN,
	[UserNotificationContentType.LINKEDIN_MESSAGE_REPLIED]: NotificationCategory.CAMPAIGN,
	[UserNotificationContentType.LINKEDIN_INVITE_ACCEPTED]: NotificationCategory.LINKEDIN,
	[UserNotificationContentType.EMAIL_OPENED]: NotificationCategory.CAMPAIGN,
	[UserNotificationContentType.EMAIL_LINK_CLICKED]: NotificationCategory.CAMPAIGN,
	[UserNotificationContentType.CONNECTION_SYNC_DONE]: NotificationCategory.CONNECTION,
	[UserNotificationContentType.CALENDAR_INVITE_RECEIVED]: NotificationCategory.CAMPAIGN,
	[UserNotificationContentType.TICKET_STAFF_REPLY]: NotificationCategory.SYSTEM,
	[UserNotificationContentType.TICKET_STATUS_CHANGED]: NotificationCategory.SYSTEM,
	[UserNotificationContentType.TICKET_ASSIGNED]: NotificationCategory.SYSTEM,
	[UserNotificationContentType.TICKET_CUSTOMER_REPLY]: NotificationCategory.SYSTEM,
	[UserNotificationContentType.CREDITS_LOW]: NotificationCategory.PAYMENT,
	[UserNotificationContentType.CREDITS_EMPTY]: NotificationCategory.PAYMENT,
	[UserNotificationContentType.TRACKER_JOB_CHANGE_NO_CREDITS]: NotificationCategory.PAYMENT,
};

/** Priority color mapping for UI indicators */
export const PRIORITY_COLORS: Record<NotificationPriority, string> = {
	[NotificationPriority.LOW]: "bg-gray-400",
	[NotificationPriority.MEDIUM]: "bg-blue-500",
	[NotificationPriority.HIGH]: "bg-orange-500",
	[NotificationPriority.URGENT]: "bg-red-500",
};
